import { FC, PropsWithChildren, useRef } from 'react';
import { twJoin } from 'tailwind-merge';
import { v4 as uuidv4 } from 'uuid';

import {
  Checkbox,
  Icon,
  IconButton,
  Ryecon,
  RyeconCaretRight,
} from '@dbt-labs/sourdough';

export interface FilterToggleProps extends PropsWithChildren<{}> {
  isChecked: boolean | undefined;
}

interface BaseFilterToggleProps extends FilterToggleProps {
  ryecon?: Ryecon;
  onToggle: () => void;
  onDive?: () => void;
  compact?: boolean;
  disabled?: boolean;
  checkBoxTestId?: string;
  count?: number;
}

export const BaseFilterToggle: FC<BaseFilterToggleProps> = ({
  ryecon,
  isChecked,
  onToggle,
  onDive,
  compact,
  disabled = false,
  children,
  checkBoxTestId,
  count,
}) => {
  const formId = useRef<string>(uuidv4());
  return (
    <div className="my-1 flex flex-col text-sm text-fgAlt">
      <div className="flex h-8">
        <label
          className={twJoin('flex grow items-center', compact ? 'gap-2' : 'gap-4')}
        >
          <Checkbox
            className="flex-0"
            inputClassName="my-1" // Sourdough applies `mt-1` which prevents vertical alignment without this
            id={formId.current}
            name={formId.current}
            disabled={disabled}
            isEdit
            isInline
            checked={!!isChecked}
            onChange={onToggle}
            testId={checkBoxTestId}
          />
          <div
            className={twJoin(
              'flex flex-1 items-center',
              compact ? 'gap-1' : 'gap-2',
              isChecked ? 'text-fgMain' : 'text-fgDecorative',
            )}
          >
            {ryecon && <Icon ryecon={ryecon} className="flex-0 text-fgMain" />}
            {children}
            {count !== undefined && (
              <span className="ml-auto text-xs text-fgDecorative">({count})</span>
            )}
          </div>
        </label>

        {onDive && (
          <IconButton
            size="sm"
            className="flex-0 p-3 pr-0"
            ryecon={RyeconCaretRight}
            onClick={onDive}
          />
        )}
      </div>
    </div>
  );
};
